import {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { SearchOutlineIcon } from '../../icons/inputIcons'
import XIcon from '../../icons/IconLib/XIcon'

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  className?: string
  leadingIcon?: ReactNode | null
  showKbd?: boolean
  kbdKey?: string
  enableShortcut?: boolean
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      leadingIcon,
      showKbd = true,
      kbdKey = 'K',
      enableShortcut = true,
      ...inputProps
    },
    forwardedRef,
  ) => {
    const hasLeading = leadingIcon !== null
    const renderedLeading = leadingIcon ?? <SearchOutlineIcon />
    const innerRef = useRef<HTMLInputElement | null>(null)
    const [isMac, setIsMac] = useState(false)
    const hasText = !!inputProps.value?.toString().length

    // Чистим через нативный setter + событие input, чтобы у контролирующего
    // родителя отработал обычный onChange с пустым значением — без нового пропса.
    const handleClear = () => {
      const node = innerRef.current
      if (!node) return
      const setter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
      )?.set
      setter?.call(node, '')
      node.dispatchEvent(new Event('input', { bubbles: true }))
      node.focus()
    }

    const setRef = (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof forwardedRef === 'function') forwardedRef(node)
      else if (forwardedRef) forwardedRef.current = node
    }

    useEffect(() => {
      const ua = navigator.userAgent || ''
      const mac = /Mac|iPhone|iPad|iPod/i.test(ua)
      setIsMac(mac)

      if (!enableShortcut) return
      const handler = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && document.activeElement === innerRef.current) {
          innerRef.current?.blur()
          return
        }
        if (mac) {
          // Cmd+K on Mac — works reliably.
          if (e.metaKey && e.key.toLowerCase() === kbdKey.toLowerCase()) {
            e.preventDefault()
            innerRef.current?.focus()
            innerRef.current?.select()
          }
          return
        }
        // Windows/Linux: Ctrl+K is reserved by the browser (omnibox/search).
        // Use "/" instead — GitHub/Slack convention. Only when not already typing.
        const target = e.target as HTMLElement | null
        const tag = target?.tagName
        const isTyping =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          (target?.isContentEditable ?? false)
        if (!isTyping && e.key === '/') {
          e.preventDefault()
          innerRef.current?.focus()
          innerRef.current?.select()
        }
      }
      window.addEventListener('keydown', handler)
      return () => window.removeEventListener('keydown', handler)
    }, [enableShortcut, kbdKey])

    return (
      <Wrapper
        className={className}
        role='search'
        $hasLeading={hasLeading}
      >
        {hasLeading && <IconSlot aria-hidden>{renderedLeading}</IconSlot>}

        <Field
          ref={setRef}
          type='text'
          autoComplete='off'
          {...inputProps}
        />

        {showKbd && hasText && (
          <ClearButton
            type='button'
            aria-label='clear search'
            onClick={handleClear}
          >
            <XIcon size={12} />
          </ClearButton>
        )}
        {showKbd && !hasText && (
          <KbdButton
            type='button'
            aria-label='keyboard shortcut'
            onClick={() => innerRef.current?.focus()}
          >
            {isMac ? (
              <>
                <kbd>⌘</kbd>
                <kbd>{kbdKey}</kbd>
              </>
            ) : (
              <kbd>/</kbd>
            )}
          </KbdButton>
        )}
      </Wrapper>
    )
  },
)

SearchInput.displayName = 'SearchInput'

export default SearchInput

const Wrapper = styled.div<{ $hasLeading: boolean }>`
  width: 100%;
  min-height: 2.875rem;
  display: grid;
  grid-template-columns: ${({ $hasLeading }) =>
    $hasLeading ? 'auto 1fr auto' : '1fr auto'};
  align-items: center;
  gap: 0.75rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 0.875rem;
  padding: 0 1rem 0 0.625rem;
  direction: rtl;
  transition:
    border-color ${({ theme }) => theme.motion.fast} ease,
    box-shadow ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.inputBorderHover};
  }

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.ink};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 2.5rem;
    padding: 0 0.75rem 0 0.5rem;
    gap: 0.5rem;
  }
`

const IconSlot = styled.span`
  color: ${({ theme }) => theme.colors.icon};
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
`

const Field = styled.input`
  border: 0;
  outline: 0;
  background: transparent;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  color: ${({ theme }) => theme.colors.ink};
  width: 100%;
  padding: 0.375rem 0;
  direction: rtl;

  &::placeholder {
    color: ${({ theme }) => theme.colors.ink4};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.lg};
  }
`

const KbdButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: 1.75rem;
  padding: 0 0.5rem;
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.colors.line2};
  color: ${({ theme }) => theme.colors.ink3};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid ${({ theme }) => theme.colors.line};
  transition: all ${({ theme }) => theme.motion.fast} ease;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  ${Wrapper}:focus-within & {
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  kbd {
    font-family: inherit;
    font-size: inherit;
    background: transparent;
    color: inherit;
    padding: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: none;
  }
`

// Тот же вид, что у kbd-чипа, но крестик нужен и на мобиле (чип там скрыт).
const ClearButton = styled(KbdButton)`
  padding: 0 0.4rem;

  svg {
    display: block;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: inline-flex;
    height: 1.5rem;
  }
`
